import { useCallback, useMemo, useState } from 'react';

import {
  FieldTypes,
  ValidationRules,
  type FieldDefinitionRequest,
  type ScalarFieldType,
  type TableColumnRequest,
} from '../api';

/**
 * Local editor state for the draggable field list. The component owns this state;
 * persistence happens via the `update` mutation when the user clicks "Guardar".
 *
 * Group fields nest children as a recursive list of drafts (one level deep — the domain rejects
 * Group inside Group). Children carry their own `draftId` for stable dnd-kit reordering; both
 * `draftId` and the recursive shape are stripped by `toApiPayload` before hitting the wire.
 */
export interface FieldDefinitionDraft extends Omit<FieldDefinitionRequest, 'children'> {
  /** Stable identifier for dnd-kit — never sent to the server. */
  draftId: string;
  /** Children for Group fields. Always null when type !== 'Group'. */
  children?: FieldDefinitionDraft[] | null;
}

function nextDraftId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `field-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function emptyField(): FieldDefinitionDraft {
  return {
    draftId: nextDraftId(),
    name: '',
    type: FieldTypes.String,
    required: false,
    critical: false,
    pattern: null,
    validationRule: ValidationRules.None,
    childFields: null,
    hint: null,
    columns: null,
    children: null,
  };
}

/** Default for a freshly added column inside a Table field. */
export function emptyColumn(): TableColumnRequest {
  return {
    name: '',
    type: FieldTypes.String,
    required: false,
    pattern: null,
    hint: null,
  };
}

/**
 * Default for a freshly added child inside a Group field. Children cannot themselves be Groups
 * (V1 domain cap), so we default to String like top-level scalar fields.
 */
export function emptyGroupChild(): FieldDefinitionDraft {
  return {
    draftId: nextDraftId(),
    name: '',
    type: FieldTypes.String,
    required: false,
    critical: false,
    pattern: null,
    validationRule: ValidationRules.None,
    childFields: null,
    hint: null,
    columns: null,
    children: null,
  };
}

export interface FieldDefinitionStateApi {
  fields: FieldDefinitionDraft[];
  setFields: (next: FieldDefinitionDraft[]) => void;
  add: () => void;
  remove: (draftId: string) => void;
  update: (draftId: string, patch: Partial<Omit<FieldDefinitionRequest, 'children'>>) => void;
  reorder: (from: number, to: number) => void;
  /** Replace the children array on a field (or null to clear). Use for type-switch transitions. */
  setChildren: (draftId: string, children: FieldDefinitionDraft[] | null) => void;
  /** Append a child to a Group field by its draftId. */
  addChild: (parentDraftId: string) => void;
  /** Remove one child of a Group by its child draftId. */
  removeChild: (parentDraftId: string, childDraftId: string) => void;
  /** Patch one child of a Group by its child draftId. */
  updateChild: (
    parentDraftId: string,
    childDraftId: string,
    patch: Partial<Omit<FieldDefinitionRequest, 'children'>>,
  ) => void;
  /** Strips the `draftId` (recursively for children) before sending to the API. */
  toApiPayload: () => FieldDefinitionRequest[];
}

export function useFieldDefinitionState(initial: FieldDefinitionDraft[]): FieldDefinitionStateApi {
  const [fields, setFields] = useState<FieldDefinitionDraft[]>(initial);

  const add = useCallback(() => {
    setFields((prev) => [...prev, emptyField()]);
  }, []);

  const remove = useCallback((draftId: string) => {
    setFields((prev) => prev.filter((f) => f.draftId !== draftId));
  }, []);

  const update = useCallback((
    draftId: string,
    patch: Partial<Omit<FieldDefinitionRequest, 'children'>>,
  ) => {
    setFields((prev) => prev.map((f) => (f.draftId === draftId ? { ...f, ...patch } : f)));
  }, []);

  const setChildren = useCallback((draftId: string, children: FieldDefinitionDraft[] | null) => {
    setFields((prev) => prev.map((f) => (f.draftId === draftId ? { ...f, children } : f)));
  }, []);

  const addChild = useCallback((parentDraftId: string) => {
    setFields((prev) => prev.map((f) => {
      if (f.draftId !== parentDraftId) {
        return f;
      }
      const existing = f.children ?? [];
      return { ...f, children: [...existing, emptyGroupChild()] };
    }));
  }, []);

  const removeChild = useCallback((parentDraftId: string, childDraftId: string) => {
    setFields((prev) => prev.map((f) => {
      if (f.draftId !== parentDraftId || f.children == null) {
        return f;
      }
      const next = f.children.filter((c) => c.draftId !== childDraftId);
      return { ...f, children: next.length === 0 ? null : next };
    }));
  }, []);

  const updateChild = useCallback((
    parentDraftId: string,
    childDraftId: string,
    patch: Partial<Omit<FieldDefinitionRequest, 'children'>>,
  ) => {
    setFields((prev) => prev.map((f) => {
      if (f.draftId !== parentDraftId || f.children == null) {
        return f;
      }
      const next = f.children.map((c) => (c.draftId === childDraftId ? { ...c, ...patch } : c));
      return { ...f, children: next };
    }));
  }, []);

  const reorder = useCallback((from: number, to: number) => {
    setFields((prev) => {
      if (from < 0 || from >= prev.length || to < 0 || to >= prev.length || from === to) {
        return prev;
      }
      const copy = prev.slice();
      const [moved] = copy.splice(from, 1);
      if (moved === undefined) {
        return prev;
      }
      copy.splice(to, 0, moved);
      return copy;
    });
  }, []);

  const toApiPayload = useCallback((): FieldDefinitionRequest[] => {
    return fields.map(stripDraft);
  }, [fields]);

  return useMemo<FieldDefinitionStateApi>(
    () => ({
      fields, setFields, add, remove, update, reorder,
      setChildren, addChild, removeChild, updateChild,
      toApiPayload,
    }),
    [fields, add, remove, update, reorder, setChildren, addChild, removeChild, updateChild, toApiPayload],
  );
}

function stripDraft(draft: FieldDefinitionDraft): FieldDefinitionRequest {
  const { draftId: _draftId, children, ...rest } = draft;
  return {
    ...rest,
    children: children == null || children.length === 0 ? null : children.map(stripDraft),
  };
}

interface ResponseFieldShape {
  name: string;
  type: string;
  required: boolean;
  critical: boolean;
  pattern?: string | null;
  validationRule: string;
  childFields?: string[] | null;
  hint?: string | null;
  columns?: ReadonlyArray<{
    name: string;
    type: string;
    required: boolean;
    pattern?: string | null;
    hint?: string | null;
  }> | null;
  children?: ReadonlyArray<ResponseFieldShape> | null;
}

/** Adapts a server-side field list into editor drafts (assigns local IDs, recursively for groups). */
export function fieldsFromResponse(
  fields: ReadonlyArray<ResponseFieldShape>,
): FieldDefinitionDraft[] {
  return fields.map(fieldFromResponse);
}

function fieldFromResponse(f: ResponseFieldShape): FieldDefinitionDraft {
  return {
    draftId: nextDraftId(),
    name: f.name,
    type: (f.type as FieldDefinitionDraft['type']) ?? FieldTypes.String,
    required: f.required,
    critical: f.critical,
    pattern: f.pattern ?? null,
    validationRule: (f.validationRule as FieldDefinitionDraft['validationRule']) ?? ValidationRules.None,
    childFields: f.childFields ?? null,
    hint: f.hint ?? null,
    columns: f.columns == null
      ? null
      : f.columns.map((c) => ({
          name: c.name,
          type: (c.type as ScalarFieldType) ?? FieldTypes.String,
          required: c.required,
          pattern: c.pattern ?? null,
          hint: c.hint ?? null,
        })),
    children: f.children == null || f.children.length === 0
      ? null
      : f.children.map(fieldFromResponse),
  };
}
